import { useEffect, useState } from 'react'

import { Agreement01Icon } from '@hugeicons/react'
import clsx from 'clsx'

import { useResourceRecordsContext } from '@/shared/hooks'
import { Badge } from '@/shared/ui/Badge'
import { Card } from '@/shared/ui'
import Image from '@/shared/ui/AppImage'
import {
  FeaturedListCardStrip,
  featuredListCardClassName
} from '@/shared/ui/FeaturedListCardStrip'

import { PartnerCheckInMarker } from './components/PartnerCheckInMarker'
import { HeartReactionButton } from './HeartReactionButton'
import { compactPartnerCategories } from './lib/partnerCategories'
import { buildPartnerPageHref } from './lib/partnerRoute'

export const DigitalProductPartnersCard = ({
  slug,
  id,
  name,
  logo,
  meta,
  partner_categories,
  my_reactions,
  reactions: reactionsProps,
  checkin_active,
  finished,
  isHighlight
}) => {
  const {
    refetch,
  } = useResourceRecordsContext()
  const [localReactions, setLocalReactions] = useState(reactionsProps)
  const [userReaction, setUserReaction] = useState(() =>
    my_reactions?.includes?.('like') ? 'like' : null
  )

  useEffect(() => {
    setLocalReactions(reactionsProps)
  }, [reactionsProps])

  useEffect(() => {
    setUserReaction(my_reactions?.includes?.('like') ? 'like' : null)
  }, [my_reactions])

  const handleReactionChange = (newCounts, newUserReaction) => {
    setLocalReactions(newCounts)
    setUserReaction(newUserReaction)
    refetch?.()
  }

  const logoUrl = logo?.file?.url
  // Um rótulo e um contador: a coluna do meio tem 140px a 375px e o rótulo
  // `.badge` gasta ~7,5px por caractere. A conta inteira está em
  // `lib/partnerCategories.ts`.
  const { names: categoryNames, visible: visibleCategories, overflowCount } =
    compactPartnerCategories(partner_categories)
  const headline = meta?.headline

  return (
    <Card
      href={buildPartnerPageHref(slug || id)}
      flush={isHighlight}
      className={clsx(
        'card-gamification cursor-pointer',
        isHighlight
          ? featuredListCardClassName('partner')
          : 'shadow-sm transition-shadow hover:shadow-md'
      )}
    >
      {isHighlight && (
        <FeaturedListCardStrip label="Top curtidas" tone="partner" />
      )}

      <Card.Body>
        <div className="flex items-stretch gap-2 sm:gap-3">
          <div className="flex aspect-[4/3] h-20 shrink-0 self-start overflow-hidden rounded-[0.625rem] border border-brand/10 bg-white">
            {logoUrl ? (
              <Image
                src={logoUrl}
                alt={name}
                width={107}
                height={80}
                className="h-full w-full object-cover object-center"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-white text-brand">
                <Agreement01Icon size={28} variant="duotone" aria-hidden />
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            {categoryNames.length > 0 && (
              <>
                {/* O contador não diz QUAIS são as outras, então o que o
                    leitor de tela recebe é a lista inteira, e a linha visual
                    sai da árvore de acessibilidade.

                    O truncamento vai no `<span>` do rótulo, não na caixa do
                    `.badge`: a caixa é `inline-flex`, e `text-overflow` só
                    age sobre conteúdo inline direto de uma caixa com
                    `overflow: hidden` — item de flex é blocificado. Com o
                    `truncate` na caixa, o nome longo era cortado a seco, sem
                    reticências, que é o defeito que esta linha existe para
                    corrigir. O `min-w-0` no filho é o que o deixa encolher
                    abaixo do conteúdo. */}
                <div
                  aria-hidden
                  data-testid="partner-categories"
                  className="flex min-w-0 items-center gap-1"
                >
                  <Badge
                    variant="available"
                    tone="ghost"
                    size="sm"
                    showIcon={false}
                    className="min-w-0 overflow-hidden [&>span]:min-w-0 [&>span]:truncate"
                  >
                    {visibleCategories[0]}
                  </Badge>

                  {overflowCount > 0 && (
                    <Badge
                      variant="available"
                      tone="ghost"
                      size="sm"
                      showIcon={false}
                      className="shrink-0"
                    >
                      +{overflowCount}
                    </Badge>
                  )}
                </div>
                <span className="sr-only">{categoryNames.join(', ')}</span>
              </>
            )}

            <h3 className="m-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900">
              {name}
            </h3>

            {headline && (
              <p className="m-0 line-clamp-2 text-2xs leading-snug text-gray-700">
                {headline}
              </p>
            )}
          </div>

          <div className="flex min-h-20 w-[70px] shrink-0 flex-col items-end justify-center gap-1.5 overflow-visible">
            <PartnerCheckInMarker
              id={id}
              slug={slug}
              checkin_active={checkin_active}
              finished={finished}
            />
            <HeartReactionButton
              id={id}
              reactableType="Partner"
              reacted={userReaction}
              reactions={localReactions}
              onReactionChange={handleReactionChange}
              likeAriaLabel="Curtir parceiro"
              unlikeAriaLabel="Remover curtida do parceiro"
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
